/**
 * @author      sam@zumper.com (Sam Sherman)
 * @copyright   Copyright (c) 2018, Zumper
 * @description helper function to track monetized lead conversion
 */

import type { FloorplanListing, Listable, Property } from '@zcode/state-type'
import { isNumber } from '@zcode/utils'
import { logger } from '../../../../logger'

const MONETIZED_LEAD_EVENT = 'monetizedLead'
const NON_MONETIZED_LEAD_EVENT = 'nonMonetizedLead'

function hasDataLayer<W extends Window>(
  window: W
): window is W & { dataLayer: { push: (event: { event: string }) => void } } {
  // @ts-expect-error
  return !!window.dataLayer
}

type Props = {
  entity: Listable | Property
  floorplan: FloorplanListing | undefined
} & (
  | { isMulti?: false; multiListables?: never }
  | { isMulti: true; multiListables: readonly Listable[] }
)

const trackLeadConversion = ({ entity, floorplan, isMulti, multiListables }: Props) => {
  if (typeof window === 'undefined' || !hasDataLayer(window)) {
    logger.error('trackLeadConversion called without window.dataLayer')
    return
  }

  if (isMulti && multiListables) {
    for (const listable of multiListables) {
      if (listable.isMonetizedM4) {
        const monetizedLeadEvent: { event: typeof MONETIZED_LEAD_EVENT; pricePerBedroom?: number } =
          {
            event: MONETIZED_LEAD_EVENT,
          }
        const pricePerBedroom = getPricePerBedroom(listable)
        if (pricePerBedroom) {
          monetizedLeadEvent.pricePerBedroom = pricePerBedroom
        }
        window.dataLayer.push(monetizedLeadEvent)
      } else {
        window.dataLayer.push({ event: NON_MONETIZED_LEAD_EVENT })
      }
      if (listable.feedName === 'listhub' && listable.providerId) {
        window.listhubTrack?.('AGENT_EMAIL_SENT', listable.providerId)
      }
    }
  } else if (entity) {
    if (entity.isMonetizedM4) {
      const monetizedLeadEvent: { event: typeof MONETIZED_LEAD_EVENT; pricePerBedroom?: number } = {
        event: MONETIZED_LEAD_EVENT,
      }
      const { isBuilding } = entity
      const pricePerBedroom = getPricePerBedroom(isBuilding && floorplan ? floorplan : entity)
      if (pricePerBedroom) {
        monetizedLeadEvent.pricePerBedroom = pricePerBedroom
      }
      window.dataLayer.push(monetizedLeadEvent)
    } else {
      window.dataLayer.push({ event: NON_MONETIZED_LEAD_EVENT })
    }
    if (entity.feedName === 'listhub' && entity.providerId) {
      window.listhubTrack?.('AGENT_EMAIL_SENT', entity.providerId)
    }
  }
}

const getPricePerBedroom = (entity: Property | Listable) => {
  const { bedrooms, minBedrooms, minPrice, maxBedrooms, maxPrice, price } = entity
  // listing/floor plan have price but listable only has minPrice/maxPrice. same with bedrooms vs minBedrooms/maxBedrooms
  const priceResult = price || minPrice || maxPrice
  const bedroomsResult = bedrooms ?? minBedrooms ?? maxBedrooms
  if (isNumber(priceResult) && isNumber(bedroomsResult)) {
    if (bedroomsResult === 0) {
      // we are considering a studio as 7/10ths of a bedroom
      return Math.round(priceResult / 0.7)
    }
    return Math.round(priceResult / bedroomsResult)
  }
  return undefined
}

export default trackLeadConversion
